/**
 * Green Stay Service Level Normalization
 * Phase P2.2-10: Sustainability Engine v1
 *
 * Unifies Eco Mode, Skip Cleaning, and Towels Only into a consistent
 * room service policy system with clear rules and no contradictions.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Room service mode for a specific date
 * Only ONE mode per room per day
 */
export enum RoomServiceMode {
  FULL_CLEAN = 'FULL_CLEAN',       // Standard housekeeping
  TOWELS_ONLY = 'TOWELS_ONLY',     // Refresh towels only (implies no cleaning)
  NO_SERVICE = 'NO_SERVICE',        // Skip all housekeeping
}

/**
 * Service policy for a specific date
 */
export interface GreenStayServicePolicy {
  date: string;                    // ISO date
  mode: RoomServiceMode;
  ecoModeEnabled: boolean;         // Whether eco mode is on (modifier, not service mode)
}

/**
 * Estimated resource savings from eco-actions
 */
export interface EcoSavings {
  water: number;        // Liters
  laundry: number;      // Kg
  energy: number;       // kWh
  co2: number;          // Kg
}

/**
 * Eco event for logging and activity feed
 */
export interface EcoEvent {
  eventId: string;
  roomNumber: string;
  stayId: string;
  date: string;                    // ISO date affected
  serviceMode: RoomServiceMode;
  action: 'ECO_ENABLED' | 'SKIP_CLEANING' | 'TOWELS_ONLY' | 'FULL_CLEAN_RESTORED' | 'ECO_DISABLED';
  estimatedSavings: EcoSavings;
  createdAt: Date;
}

// =============================================================================
// SERVICE MODE RULES
// =============================================================================

/**
 * Determine service mode for a date based on skip/towels arrays
 *
 * RULES:
 * - Towels Only takes precedence (auto-implies NO_SERVICE)
 * - Skip Cleaning = NO_SERVICE
 * - Default = FULL_CLEAN
 * - Only ONE mode per date
 */
export function getServiceModeForDate(
  date: string,
  skipCleaningDays: string[],
  towelsOnlyDays: string[]
): RoomServiceMode {
  // Rule 1: Towels Only takes precedence
  if (towelsOnlyDays.includes(date)) {
    return RoomServiceMode.TOWELS_ONLY;
  }

  // Rule 2: Skip Cleaning = NO_SERVICE
  if (skipCleaningDays.includes(date)) {
    return RoomServiceMode.NO_SERVICE;
  }

  // Rule 3: Default = FULL_CLEAN
  return RoomServiceMode.FULL_CLEAN;
}

/**
 * Get all service policies for stay dates
 */
export function getServicePolicies(
  stayDates: string[],
  skipCleaningDays: string[],
  towelsOnlyDays: string[],
  ecoModeEnabled: boolean
): GreenStayServicePolicy[] {
  return stayDates.map(date => ({
    date,
    mode: getServiceModeForDate(date, skipCleaningDays, towelsOnlyDays),
    ecoModeEnabled,
  }));
}

/**
 * Normalize towels-only selection to also skip cleaning
 * (Towels Only implies NO_SERVICE for cleaning)
 */
export function normalizeTowelsOnlyMode(
  towelsOnlyDays: string[],
  skipCleaningDays: string[]
): string[] {
  // All towels-only days should also be in skip-cleaning
  // But we don't modify arrays - service mode determination handles this
  // This function is for UI consistency checks
  const missingInSkip = towelsOnlyDays.filter(d => !skipCleaningDays.includes(d));
  return [...skipCleaningDays, ...missingInSkip];
}

/**
 * Check if service mode requires housekeeping
 */
export function requiresHousekeeping(mode: RoomServiceMode): boolean {
  return mode === RoomServiceMode.FULL_CLEAN;
}

/**
 * Check if service mode allows towel refresh
 */
export function allowsTowelRefresh(mode: RoomServiceMode): boolean {
  return mode === RoomServiceMode.FULL_CLEAN || mode === RoomServiceMode.TOWELS_ONLY;
}

// =============================================================================
// ECO SAVINGS ESTIMATION
// =============================================================================

/**
 * Estimate resource savings for a service mode vs FULL_CLEAN
 * Simple estimates for guest feedback (not scientifically precise)
 */
export function estimateSavings(mode: RoomServiceMode): EcoSavings {
  // Updated to match sustainability dashboard baseline constants (P2.3-2 v2)
  // v2: More realistic towels-only savings (~33% of skip cleaning, not 75%)
  switch (mode) {
    case RoomServiceMode.NO_SERVICE:
      // Full clean avoided: 60L water, 2kWh energy, 0.6kg CO2, 1 laundry cycle
      return {
        water: 60,      // 60L per full room cleaning
        laundry: 1,     // 1 full laundry cycle avoided
        energy: 2,      // 2kWh (cleaning equipment + laundry)
        co2: 0.6,       // 0.6kg CO2
      };

    case RoomServiceMode.TOWELS_ONLY:
      // Partial clean vs full clean savings: (60-40)L water, (2-1.2)kWh, (0.6-0.4)kg CO2
      // Housekeeping still visits, does laundry, empties trash - only skips deep cleaning
      return {
        water: 20,      // 20L saved (full 60L - towels only 40L)
        laundry: 0.3,   // 0.3 cycles saved (full 1 - towels 0.7)
        energy: 0.8,    // 0.8kWh saved (full 2 - towels 1.2)
        co2: 0.2,       // 0.2kg CO2 saved (full 0.6 - towels 0.4)
      };

    case RoomServiceMode.FULL_CLEAN:
    default:
      return {
        water: 0,
        laundry: 0,
        energy: 0,
        co2: 0,
      };
  }
}

/**
 * Sum savings across multiple days
 */
export function sumSavings(savingsArray: EcoSavings[]): EcoSavings {
  return savingsArray.reduce(
    (total, savings) => ({
      water: total.water + savings.water,
      laundry: total.laundry + savings.laundry,
      energy: total.energy + savings.energy,
      co2: total.co2 + savings.co2,
    }),
    { water: 0, laundry: 0, energy: 0, co2: 0 }
  );
}

/**
 * Calculate total savings for all eco-days in stay
 */
export function calculateStaySavings(
  skipCleaningDays: string[],
  towelsOnlyDays: string[]
): EcoSavings {
  const allSavings: EcoSavings[] = [];

  // Get unique dates (towels takes precedence)
  const allDates = new Set([...skipCleaningDays, ...towelsOnlyDays]);

  allDates.forEach(date => {
    const mode = getServiceModeForDate(date, skipCleaningDays, towelsOnlyDays);
    allSavings.push(estimateSavings(mode));
  });

  return sumSavings(allSavings);
}

// =============================================================================
// SERVICE MODE DISPLAY
// =============================================================================

/**
 * Get human-readable label for service mode
 */
export function getServiceModeLabel(mode: RoomServiceMode): string {
  switch (mode) {
    case RoomServiceMode.FULL_CLEAN:
      return 'Full Cleaning';
    case RoomServiceMode.TOWELS_ONLY:
      return 'Towels Only';
    case RoomServiceMode.NO_SERVICE:
      return 'No Service';
  }
}

/**
 * Get emoji icon for service mode
 */
export function getServiceModeIcon(mode: RoomServiceMode): string {
  switch (mode) {
    case RoomServiceMode.FULL_CLEAN:
      return '🧹';
    case RoomServiceMode.TOWELS_ONLY:
      return '🏖️';
    case RoomServiceMode.NO_SERVICE:
      return '♻️';
  }
}

/**
 * Get color class for service mode
 */
export function getServiceModeColor(mode: RoomServiceMode): string {
  switch (mode) {
    case RoomServiceMode.FULL_CLEAN:
      return 'text-slate-400';
    case RoomServiceMode.TOWELS_ONLY:
      return 'text-blue-400';
    case RoomServiceMode.NO_SERVICE:
      return 'text-green-400';
  }
}

// =============================================================================
// STATE VALIDATION
// =============================================================================

/**
 * Validate and auto-correct green stay state
 * Ensures no contradictory states exist
 */
export function validateGreenStayState(state: {
  skipCleaningDays: string[];
  towelsOnlyDays: string[];
}): {
  skipCleaningDays: string[];
  towelsOnlyDays: string[];
  corrected: boolean;
} {
  const { skipCleaningDays, towelsOnlyDays } = state;
  let corrected = false;

  // Remove duplicates
  const uniqueSkip = Array.from(new Set(skipCleaningDays));
  const uniqueTowels = Array.from(new Set(towelsOnlyDays));

  if (uniqueSkip.length !== skipCleaningDays.length ||
      uniqueTowels.length !== towelsOnlyDays.length) {
    corrected = true;
  }

  return {
    skipCleaningDays: uniqueSkip,
    towelsOnlyDays: uniqueTowels,
    corrected,
  };
}
